// src/components/ui/Alert.js
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleExclamation,
  faTriangleExclamation,
  faCircleInfo,
  faCircleCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import "../../style/Alert.css";

const ICONS = {
  error: faCircleExclamation,
  warning: faTriangleExclamation,
  info: faCircleInfo,
  success: faCircleCheck,
};

const MAX_MESSAGE_LEN = 300;

function truncate(msg) {
  const s = typeof msg === "string" ? msg : String(msg ?? "");
  return s.length > MAX_MESSAGE_LEN ? `${s.slice(0, MAX_MESSAGE_LEN)}…` : s;
}

const Alert = ({
  message,
  title,
  severity = "error",
  onClose,
  compact = false,
  className = "",
}) => {
  const { t } = useTranslation();
  if (!message) return null;

  const icon = ICONS[severity] || ICONS.info;

  // "Soydev" fix: don’t treat arbitrary runtime strings like translation keys.
  // Use defaultValue so unknown keys fall back to the original string.
  const safeMessage = truncate(message);
  const renderedMessage =
    typeof safeMessage === "string"
      ? t(safeMessage, { defaultValue: safeMessage })
      : String(safeMessage);

  const renderedTitle =
    title ? t(title, { defaultValue: title }) : null;

  return (
    <div
      className={`alert-enhanced alert-enhanced--${severity} ${
        compact ? "alert-enhanced--compact" : ""
      } ${className}`.trim()}
      role="alert"
    >
      <div className="alert-enhanced__icon">
        <FontAwesomeIcon icon={icon} />
      </div>

      <div className="alert-enhanced__content">
        {renderedTitle && <div className="alert-enhanced__title">{renderedTitle}</div>}
        <div className="alert-enhanced__message">{renderedMessage}</div>
      </div>

      {onClose && (
        <button
          className="alert-enhanced__close"
          onClick={onClose}
          aria-label="Close alert"
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  );
};

Alert.propTypes = {
  message: PropTypes.string,
  title: PropTypes.string,
  severity: PropTypes.oneOf(["error", "warning", "info", "success"]),
  onClose: PropTypes.func,
  compact: PropTypes.bool,
  className: PropTypes.string,
};

export default Alert;
